import { Box, IconButton, Typography, Grid } from "@mui/material";
import { format } from "date-fns";
import React, { useState } from "react";
import BatchEmailModel, { EmailResults } from "../../core/api/email/BatchEmailModel";
import GroupIcon from "@mui/icons-material/Group";
import { useNavigate } from "react-router-dom";
import { DataGrid, GridColDef, GridRenderCellParams, GridPaginationModel } from "@mui/x-data-grid";
import { translations } from "../../translations/common";
import { tableContainerStyles, tableHeaderStyles, dataGridStyles } from "../common/styles/TableStyles";

const STATES = new Map([
  ["PAST", translations.clientTable.states.PAST],
  ["ACTIVE", translations.clientTable.states.ACTIVE],
  ["DEAD", translations.clientTable.states.DEAD],
  ["HIDDEN", translations.clientTable.states.HIDDEN],
  ["SPECIAL", translations.clientTable.states.SPECIAL],
  ["COMATOSE", translations.clientTable.states.COMATOSE],
  ["OTHER", translations.clientTable.states.OTHER],
]);

const EmailResultsTable: React.FC = () => {
  const navigate = useNavigate();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 10,
    page: 0,
  });
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<(EmailResults & { id: string })[]>([]);
  const [totalRows, setTotalRows] = useState(0);

  const handleViewRecipients = (id: string) => {
    navigate({
      pathname: `/emails/${id}`,
    });
  };

  const columns: GridColDef[] = [
    {
      field: "senderName",
      headerName: translations.emailResultsTable.columns.senderName,
      flex: 1
    },
    {
      field: "filter",
      headerName: translations.emailResultsTable.columns.filter,
      flex: 2,
      minWidth: 300,
      renderCell: (params: GridRenderCellParams<EmailResults>) => {
        const results = params.row;
        return (
          <Grid container direction="column">
            <Typography>{translations.emailResultsTable.filterLabels.filter}: {results.filter.filter}</Typography>
            <Typography>
              {translations.emailResultsTable.filterLabels.state}: {states(results.filter.state)}
            </Typography>
            <Typography>
              {translations.emailResultsTable.filterLabels.validFrom}: {results.filter.validFrom}
            </Typography>
            <Typography>
              {translations.emailResultsTable.filterLabels.validTo}: {results.filter.validTo}
            </Typography>
            <Typography>{translations.emailResultsTable.filterLabels.idFrom}: {results.filter.idFrom}</Typography>
            <Typography>{translations.emailResultsTable.filterLabels.idTo}: {results.filter.idTo}</Typography>
          </Grid>
        );
      }
    },
    {
      field: "status",
      headerName: translations.emailResultsTable.columns.status,
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<EmailResults>) => {
        const status = params.row.status;
        if (status && translations.emailResultsTable.status) {
          const translated = translations.emailResultsTable.status[status as keyof typeof translations.emailResultsTable.status];
          return <Box>{translated || status}</Box>;
        }
        return <Box>{status || ''}</Box>;
      }
    },
    {
      field: "finishedAt",
      headerName: translations.emailResultsTable.columns.finishedAt,
      flex: 1,
      minWidth: 240,
      renderCell: (params: GridRenderCellParams<EmailResults>) => {
        const finishedAt = params.row.finishedAt;
        if (finishedAt) {
          try {
            return <Box>{format(new Date(finishedAt), "yyyy-MM-dd HH:mm")}</Box>;
          } catch (e) {
            return <Box>{finishedAt}</Box>;
          }
        }
        return <Box></Box>;
      }
    },
    {
      field: "sentCount",
      headerName: translations.emailResultsTable.columns.sentCount,
      flex: 1
    },
    {
      field: "failedCount",
      headerName: translations.emailResultsTable.columns.failedCount,
      flex: 1
    },
    {
      field: "invoiceSent",
      headerName: translations.emailResultsTable.columns.invoiceSent,
      flex: 1,
      renderCell: (params: GridRenderCellParams<EmailResults>) => {
        const invoiceSent = params.row.invoiceSent;
        const key = invoiceSent ? "true" : "false";
        return <Box>{translations.emailResultsTable.invoiceSent[key]}</Box>;
      }
    },
    {
      field: "actions",
      headerName: "",
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams<EmailResults>) => (
        <IconButton
          onClick={() => handleViewRecipients(params.row.id)}
          title={translations.emailResultsTable.actions.viewRecipients}
        >
          <GroupIcon />
        </IconButton>
      )
    }
  ];

  React.useEffect(() => {
    setLoading(true);
    BatchEmailModel.sentEmails({
      page: paginationModel.page,
      size: paginationModel.pageSize,
    })
      .then((result) => {
        // Add id property to each row for DataGrid
        const rowsWithIds = result.content.map(row => ({
          ...row,
          id: row.id || `email-${row.senderName}-${row.finishedAt}`
        }));
        setRows(rowsWithIds);
        setTotalRows(result.page.totalElements);
      })
      .finally(() => setLoading(false));
  }, [paginationModel]);

  return (
    <Box sx={tableContainerStyles}>
      <Box sx={tableHeaderStyles}>
        {translations.emailResultsTable.title}
      </Box>
      <DataGrid
        rows={rows}
        columns={columns.map(col => ({ ...col, sortable: false }))}
        rowCount={totalRows}
        loading={loading}
        pageSizeOptions={[10, 20]}
        paginationModel={paginationModel}
        paginationMode="server"
        onPaginationModelChange={setPaginationModel}
        rowHeight={150}
        disableColumnMenu
        disableRowSelectionOnClick
        slots={{
          noRowsOverlay: () => (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              {translations.emailRecipientsTable.noData}
            </Box>
          ),
        }}
        sx={dataGridStyles}
      />
    </Box>
  );
};

function states(state: string | null) {
  if (state) {
    return STATES.has(state) ? STATES.get(state) : "";
  }
  return "";
}

export default EmailResultsTable;
